/**
 * Holiday campaign look: the theme tokens the API sends (worker/src/shop-campaigns.ts
 * `KIND_THEMES`), the motif drawn in inline SVG, the countdown, and the home page's banner strip.
 * Small on purpose — the home page imports it; the full campaign page is its own chunk
 * (Campaign.tsx).
 *
 * Colours are DATA from the server (light + dark palettes, contrast-checked in
 * verify:shop-campaigns), applied as CSS custom properties; Campaign.css switches between the two
 * with prefers-color-scheme. No stock photos: every motif is a few strokes of SVG.
 */
import { useEffect, useId, useState, type CSSProperties } from "react";
import type { CampaignSummary, CampaignTheme } from "./types";
import { Go, type GoFn } from "./route";
import { get } from "./shared";
import "./Campaign.css";

/** The theme as custom properties on the campaign's root element (Campaign.css reads them). */
export function themeVars(t: CampaignTheme): CSSProperties {
  const { light: l, dark: d } = t.palette;
  return {
    "--cmp-bg-l": l.bg, "--cmp-ink-l": l.ink, "--cmp-soft-l": l.soft, "--cmp-accent-l": l.accent, "--cmp-accent-ink-l": l.accentInk,
    "--cmp-bg-d": d.bg, "--cmp-ink-d": d.ink, "--cmp-soft-d": d.soft, "--cmp-accent-d": d.accent, "--cmp-accent-ink-d": d.accentInk,
  } as CSSProperties;
}

/** One 48×48 tile per motif, stroked in the accent colour. Unknown key → a plain dotted tile. */
const TILES: Record<string, string> = {
  tags: "M10 8h14l10 10-14 14L10 22zM15 13h.01M30 30h8v8h-8z",
  circuit: "M4 12h14v14h12M30 26v18M18 26h-6v14M30 4v8h14M18 12a2 2 0 1 0 0 .01M30 26a2 2 0 1 0 0 .01",
  snow: "M24 6v36M8.4 15l31.2 18M8.4 33l31.2-18M20 9l4 4 4-4M20 39l4-4 4 4",
  fireworks: "M24 24l-10-10M24 24l10-10M24 24l-10 10M24 24l10 10M24 24V8M24 24v16M24 24H8M24 24h16",
  grid: "M0 12h48M0 24h48M0 36h48M12 0v48",
  hearts: "M24 38S10 29 10 19a7 7 0 0 1 14-2 7 7 0 0 1 14 2c0 10-14 19-14 19z",
  lanterns: "M24 4v6M18 10h12l3 6v16l-3 6H18l-3-6V16zM15 22h18M21 38v4h6v-4",
  crescent: "M30 8a16 16 0 1 0 0 32 12 12 0 1 1 0-32zM38 12l1.5 3 3 .5-2.2 2 .6 3.2-2.9-1.6-2.9 1.6.6-3.2-2.2-2 3-.5z",
  stars: "M24 6l4 10 10 1-8 7 3 10-9-6-9 6 3-10-8-7 10-1z",
  eggs: "M24 6c-7 0-12 12-12 20a12 12 0 0 0 24 0c0-8-5-20-12-20zM13 24l5-3 6 3 6-3 5 3",
  flowers: "M24 18a6 6 0 1 1 0 12 6 6 0 0 1 0-12zM24 18V8M24 30v10M18 24H8M30 24h10",
  stripes: "M-6 18L18 -6M6 30L30 6M18 42L42 18M30 54L54 30",
  rays: "M24 24L24 0M24 24L48 8M24 24L48 40M24 24L24 48M24 24L0 40M24 24L0 8",
  petals: "M24 8c6 6 6 10 0 16-6-6-6-10 0-16zM24 40c-6-6-6-10 0-16 6 6 6 10 0 16zM8 24c6-6 10-6 16 0-6 6-10 6-16 0zM40 24c-6 6-10 6-16 0 6-6 10-6 16 0z",
  flames: "M24 6c4 8 12 12 12 22a12 12 0 0 1-24 0c0-6 4-8 6-12 1 4 3 6 6 6-2-6 0-12 0-16z",
  diyas: "M10 30h28c0 6-6 10-14 10s-14-4-14-10zM24 12c3 4 4 8 0 12-4-4-3-8 0-12z",
};

/** The motif as a repeating background layer. Decorative: hidden from assistive tech. */
export function Motif({ motif, className }: { motif: string; className?: string }) {
  const id = `cmp-m-${useId().replace(/[^A-Za-z0-9_-]/g, "")}`;
  const d = TILES[motif] ?? "M24 24h.01";
  return (
    <svg className={`cmp-motif${className ? ` ${className}` : ""}`} aria-hidden="true" focusable="false" width="100%" height="100%">
      <defs>
        <pattern id={id} width="48" height="48" patternUnits="userSpaceOnUse" patternTransform="rotate(-8)">
          <path d={d} fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
        </pattern>
      </defs>
      <rect width="100%" height="100%" fill={`url(#${id})`} />
    </svg>
  );
}

/** "3 days 04:12:09" — a teaser counts to the start, a live sale to the end. */
export function SaleCountdown({ to, onEnd }: { to: string; onEnd?: () => void }) {
  const end = Date.parse(to);
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const t = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(t);
  }, [to]);
  const left = Number.isFinite(end) ? Math.max(0, end - now) : 0;
  useEffect(() => { if (left === 0) onEnd?.(); }, [left === 0]); // eslint-disable-line react-hooks/exhaustive-deps
  const s = Math.floor(left / 1000);
  const days = Math.floor(s / 86400), h = Math.floor((s % 86400) / 3600), m = Math.floor((s % 3600) / 60), sec = s % 60;
  const pad = (n: number) => String(n).padStart(2, "0");
  return (
    <span className="cmp-count money" role="timer" aria-label={`${days} days ${h} hours ${m} minutes`}>
      {days > 0 && <><b>{days}</b> {days === 1 ? "day" : "days"} </>}
      {pad(h)}:{pad(m)}:{pad(sec)}
    </span>
  );
}

/**
 * The list, shared by the banners for a SHORT while only: kept 60 s, and never past the next
 * start/end in it (a teaser that has just started, or a sale that has just ended, is fetched
 * fresh), so a long-open tab does not keep showing yesterday's banners.
 */
const LIST_TTL_MS = 60_000;
let cached: { at: number; until: number; list: CampaignSummary[] } | null = null;
const freshCache = (): CampaignSummary[] | null => {
  const now = Date.now();
  return cached && now - cached.at < LIST_TTL_MS && now < cached.until ? cached.list : null;
};
export function useCampaigns(): CampaignSummary[] | null {
  const [list, setList] = useState<CampaignSummary[] | null>(freshCache);
  useEffect(() => {
    const hit = freshCache();
    if (hit) { setList(hit); return; }
    const ac = new AbortController();
    get<{ campaigns: CampaignSummary[] }>(`/shop/campaigns?after=${Math.floor(Date.now() / LIST_TTL_MS)}`, ac.signal)
      .then((d) => {
        const edges = d.campaigns.map((c) => Date.parse(c.state === "live" ? c.ends_at : c.starts_at)).filter(Number.isFinite);
        cached = { at: Date.now(), until: edges.length ? Math.min(...edges) : Number.POSITIVE_INFINITY, list: d.campaigns };
        if (!ac.signal.aborted) setList(d.campaigns);
      })
      .catch(() => { if (!ac.signal.aborted) setList([]); });
    return () => ac.abort();
  }, []);
  return list;
}

/**
 * The home page's banner: one strip per teaser/live campaign (live first), linking to /s/<slug>.
 * Shown to EVERYONE, trade included — same pages, own prices (unlike Deal of the day).
 */
export function CampaignBanner({ go, limit = 2, className = "wrap home-sec" }: { go: GoFn; limit?: number; className?: string }) {
  const list = useCampaigns();
  if (!list || list.length === 0) return null;
  return (
    <section className={`${className} cmp-banners`} aria-label="Sales on now and coming up">
      {list.slice(0, limit).map((c) => (
        <Go key={c.slug} to={{ name: "campaign", slug: c.slug }} go={go} className="cmp-banner cmp-themed" style={themeVars(c.theme)}>
          <Motif motif={c.theme.motif} />
          <span className="cmp-banner-body">
            <span className="cmp-kicker">{c.state === "live" ? "On now" : "Coming soon"}</span>
            <span className="cmp-banner-title">{c.name}: {c.headline}</span>
            <span className="cmp-banner-when">
              {c.state === "live" ? "Ends in " : "Starts in "}
              <SaleCountdown to={c.state === "live" ? c.ends_at : c.starts_at} />
            </span>
          </span>
          <span className="cmp-banner-cta">{c.state === "live" ? "Shop the sale" : "See what's coming"} <span aria-hidden="true">→</span></span>
        </Go>
      ))}
    </section>
  );
}
